import type { PhotosByEvent } from '../types';
import { HttpError, UnauthorizedError } from '../services/httpErrors';
import type { TvSlide } from './tvPlaylist';

type TvLayerIndex = 0 | 1;
export type TvPlaybackStatus = 'loading' | 'empty' | 'playing' | 'parked' | 'unpaired';

export interface TvLayer { slide: TvSlide | null; src: string | null }

export interface TvPlaybackSnapshot {
  status: TvPlaybackStatus;
  layers: readonly [TvLayer, TvLayer];
  active: TvLayerIndex;
}

interface TvPlaybackDeps {
  fetchManifest: (signal: AbortSignal) => Promise<PhotosByEvent>;
  fetchMediaBlob: (photoId: number, signal: AbortSignal) => Promise<Blob>;
  decode: (url: string) => Promise<void>;
  buildPlaylist: (manifest: PhotosByEvent) => TvSlide[];   // closes over `order`; re-run at every wrap (reshuffle)
  dwellMs: number;
  idleMs: number;
}

export interface TvPlayback {
  start: () => void;
  refreshManifest: () => void;
  dispose: () => void;
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => TvPlaybackSnapshot;
}

// release = abort if in flight, revoke if resolved
interface RingEntry { url: Promise<string>; release: () => void }

const LAYER_COUNT = 2;
const PREFETCH_AHEAD = 2;                 // N+1, N+2
const MEDIA_FAILURES_BEFORE_SKIP = 3;
// Shared with the browse app's not-paired frame (App, UnpairedScreen): one home for both TV surfaces.
export const UNPAIRED_RETRY_MS = 60_000;
export const UNPAIRED_MESSAGE = 'This TV is not paired.';
const MANIFEST_RETRY_MS = 5 * 60_000;     // only when there is no playlist to fall back on
const MANIFEST_REFRESH_FLOOR_MS = 2 * 60_000;   // visible-wake refetches are floored, not free
const EMPTY_LAYER: TvLayer = { slide: null, src: null };
const EMPTY_LAYERS: readonly [TvLayer, TvLayer] = [EMPTY_LAYER, EMPTY_LAYER];
const NOT_FOUND = 404;

const isAbortError = (error: unknown): boolean => error instanceof Error && error.name === 'AbortError';
const isNotFound = (error: unknown): boolean => error instanceof HttpError && error.status === NOT_FOUND;

/**
 * Builds the TV playback state machine: manifest load and retry, prefetch ring,
 * dwell/idle timers, and unpaired handling. It runs only inside the non-interactive
 * screensaver, so it takes no input. Returns its control surface.
 */
export function createTvPlayback(deps: TvPlaybackDeps): TvPlayback {
  let snapshot: TvPlaybackSnapshot = { status: 'loading', layers: EMPTY_LAYERS, active: 0 };
  let manifest: PhotosByEvent | null = null;
  let slides: TvSlide[] = [];
  let index = 0;
  let previousId: number | null = null;   // retained in the ring so the outgoing slide survives the crossfade
  let mediaFailures = 0;                  // consecutive, for the current index
  let generation = 0;                     // bumped by applyPlaylist/park/unpaired/dispose; older continuations bail
  let manifestController: AbortController | null = null;
  let manifestRequestedAt = 0;
  const timers = { dwell: null as number | null, idle: null as number | null, retry: null as number | null };
  const ring = new Map<number, RingEntry>();
  const listeners = new Set<() => void>();

  // The shell forwards every page console line to logcat, so these are the only on-device
  // evidence that a park, refetch or recovery happened.
  const log = (message: string) => console.info(`[tv] ${message}`);

  const emit = (next: Partial<TvPlaybackSnapshot>) => {
    if (next.status && next.status !== snapshot.status) {
      log(`status ${snapshot.status} → ${next.status}`);
    }

    const nextSnapshot: TvPlaybackSnapshot = { ...snapshot };
    Object.assign(nextSnapshot, next);
    snapshot = nextSnapshot;
    listeners.forEach((listener) => listener());
  };

  // --- timers ---------------------------------------------------------
  const clearTimer = (name: keyof typeof timers) => {
    if (timers[name] !== null) {
      window.clearTimeout(timers[name] as number);
      timers[name] = null;
    }
  };
  const setTimer = (name: keyof typeof timers, ms: number, fn: () => void) => {
    clearTimer(name);
    timers[name] = window.setTimeout(fn, ms);
  };
  const clearPlaybackTimers = () => {
    clearTimer('dwell');
    clearTimer('retry');
  };

  // --- ring (keyed by photo id) --------------------------------------
  const ensureRingEntry = (photoId: number): RingEntry => {
    const existing = ring.get(photoId);
    if (existing) {
      return existing;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;
    const url = deps.fetchMediaBlob(photoId, controller.signal).then((blob) => {
      const created = URL.createObjectURL(blob);
      // The fetch implementation ignored the abort signal.
      if (controller.signal.aborted) {
        URL.revokeObjectURL(created);
        return created;
      }

      objectUrl = created;
      return created;
    });
    const entry: RingEntry = {
      url,
      release: () => {
        controller.abort();
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          objectUrl = null;
        }
      },
    };
    // A rejected entry must not stay cached: the next fillRing refetches it, so a reconnect
    // advances within one dwell instead of replaying stale rejections.
    url.catch(() => {
      if (!controller.signal.aborted && ring.get(photoId) === entry) {
        ring.delete(photoId);
      }
    });
    ring.set(photoId, entry);
    return entry;
  };

  const wantedIds = (): Set<number> => {
    const wanted = new Set<number>();
    for (let offset = 0; offset <= PREFETCH_AHEAD && offset < slides.length; offset += 1) {
      wanted.add(slides[(index + offset) % slides.length].id);
    }

    if (previousId !== null) {
      wanted.add(previousId);
    }

    return wanted;
  };

  const fillRing = () => {
    const wanted = wantedIds();
    // evict first so the freed blob is released before the new N+2 fetch starts
    ring.forEach((entry, id) => {
      if (!wanted.has(id)) {
        entry.release();
        ring.delete(id);
      }
    });
    wanted.forEach((id) => ensureRingEntry(id));
  };

  const releaseRing = () => {
    ring.forEach((entry) => entry.release());
    ring.clear();
    previousId = null;
  };

  // --- playback -------------------------------------------------------
  const scheduleDwell = () => {
    if (snapshot.status === 'playing') {
      setTimer('dwell', deps.dwellMs, step);
    }
  };

  const showCurrentSlide = async () => {
    const startedIn = generation;
    const slide = slides[index];
    const leaving = snapshot.layers[snapshot.active].slide;
    // Retain the outgoing slide before filling, or fillRing evicts the blob still on screen.
    if (leaving && leaving.id !== slide.id) {
      previousId = leaving.id;
    }

    fillRing();
    let url: string;
    try {
      url = await ensureRingEntry(slide.id).url;
    } catch (error) {
      if (isAbortError(error) || startedIn !== generation) {
        return;
      }

      onMediaError(error);
      return;
    }

    if (startedIn !== generation) {
      return;
    }

    await deps.decode(url).catch(() => undefined);
    if (startedIn !== generation) {
      return;
    }

    mediaFailures = 0;
    const hidden = ((snapshot.active + 1) % LAYER_COUNT) as TvLayerIndex;
    const layers: [TvLayer, TvLayer] = [snapshot.layers[0], snapshot.layers[1]];
    layers[hidden] = { slide, src: url };
    emit({ status: 'playing', layers, active: hidden });
    scheduleDwell();
  };

  // Hold the current slide on any failure - never blank. 401 -> unpaired; 404 -> skip after one
  // dwell (an instant skip would loop a stale manifest at network speed); anything else -> retry
  // after one dwell, skip after MEDIA_FAILURES_BEFORE_SKIP.
  const onMediaError = (error: unknown) => {          // the rejected entry already evicted itself in ensureRingEntry()
    if (error instanceof UnauthorizedError) {
      becomeUnpaired();
      return;
    }

    if (isNotFound(error)) {
      mediaFailures = 0;
      log('media 404 — skip after dwell');
      setTimer('retry', deps.dwellMs, step);
      return;
    }

    mediaFailures += 1;
    log(`media retry ${mediaFailures}/${MEDIA_FAILURES_BEFORE_SKIP}`);
    if (mediaFailures >= MEDIA_FAILURES_BEFORE_SKIP) {
      mediaFailures = 0;
      step();
      return;
    }

    setTimer('retry', deps.dwellMs, () => { showCurrentSlide().catch(reportUnexpected); });
  };

  const step = () => {
    clearPlaybackTimers();
    if (slides.length === 0) {
      return;
    }

    const nextIndex = index + 1;
    if (nextIndex >= slides.length && manifest) {          // loop wrap -> reshuffle (no-op for chrono); ring keeps still-wanted ids
      log('playlist wrap — order rebuilt');
      slides = deps.buildPlaylist(manifest);
      index = 0;
    } else {
      index = nextIndex % slides.length;
    }

    mediaFailures = 0;
    showCurrentSlide().catch(reportUnexpected);
  };

  // --- manifest -------------------------------------------------------
  const applyPlaylist = (next: TvSlide[]) => {
    generation += 1;
    clearPlaybackTimers();
    slides = next;
    index = 0;
    mediaFailures = 0;
    if (next.length === 0) {
      releaseRing();
      emit({ status: 'empty', layers: EMPTY_LAYERS });
      setTimer('retry', MANIFEST_RETRY_MS, loadManifest);
      return;
    }

    showCurrentSlide().catch(reportUnexpected);   // status flips to playing only after the first decode; fillRing evicts what the new order no longer wants
  };

  const loadManifest = () => {
    manifestRequestedAt = Date.now();
    manifestController?.abort();
    const controller = new AbortController();
    manifestController = controller;
    deps.fetchManifest(controller.signal)
      .then((next) => {
        if (controller.signal.aborted) {
          return;
        }
        manifest = next;
        applyPlaylist(deps.buildPlaylist(next));
      })
      .catch((error) => {
        if (controller.signal.aborted || isAbortError(error)) {
          return;
        }
        if (error instanceof UnauthorizedError) {
          becomeUnpaired();
          return;
        }
        // With a playlist: keep cycling, wait for the next trigger.
        if (slides.length === 0) {
          setTimer('retry', MANIFEST_RETRY_MS, loadManifest);
        }
      });
  };

  const becomeUnpaired = () => {
    generation += 1;
    clearPlaybackTimers();
    releaseRing();
    emit({ status: 'unpaired', layers: EMPTY_LAYERS });   // the layers held revoked blob: URLs - never let them remount
    setTimer('retry', UNPAIRED_RETRY_MS, loadManifest);
  };

  // --- idle park — the spend ceiling (idleHours); terminal inside the screensaver ---
  const park = () => {
    generation += 1;
    clearPlaybackTimers();
    manifestController?.abort();
    emit({ status: 'parked' });                               // ring kept until dispose; park is terminal
  };

  // The page becomes visible again when the dream restarts after standby, so refetch there or
  // an upload stays invisible until the next screensaver run. Parked stays fetch-free;
  // unpaired owns its own retry.
  const refreshManifest = () => {
    if (snapshot.status === 'parked' || snapshot.status === 'unpaired') {
      return;
    }

    if (Date.now() - manifestRequestedAt < MANIFEST_REFRESH_FLOOR_MS) {
      return;
    }

    log('manifest refresh (visible)');
    loadManifest();
  };

  const reportUnexpected = (error: unknown) => console.error('TV playback failure', error);

  const dispose = () => {
    generation += 1;
    clearPlaybackTimers();
    clearTimer('idle');
    manifestController?.abort();
    releaseRing();
  };

  return {
    start: () => {
      setTimer('idle', deps.idleMs, park);
      loadManifest();
    },
    refreshManifest,
    dispose,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => snapshot,
  };
}
